import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Icon, type IconName } from '@/components/icons/Icon';

/**
 * Button.
 *
 * §11: touch targets ≥ 44px, primary actions in the bottom third on mobile.
 * The 44px floor is on the `md` size and is not negotiable — it is the
 * difference between a rider tapping "Collect payment" once at a gate in the
 * rain and tapping it four times.
 *
 * Variants are semantic, not decorative (§11). `ok` is green because the action
 * confirms something verified; `bad` is red because the action blocks or
 * destroys. There is no "success-coloured" button for an ordinary save.
 */

type Variant = 'primary' | 'ghost' | 'quiet' | 'grey' | 'ok' | 'bad' | 'plain';
type Size = 'xs' | 'sm' | 'md' | 'lg';

const variants: Record<Variant, string> = {
  primary: 'bg-blue text-white hover:bg-blue-700 active:bg-blue-700',
  ghost: 'bg-transparent text-blue border-[1.5px] border-blue hover:bg-blue-tint-2',
  quiet: 'bg-blue-tint text-blue hover:bg-blue-tint/70',
  grey: 'bg-white text-muted border border-line hover:bg-paper',
  ok: 'bg-verified text-white hover:brightness-95',
  bad: 'bg-danger text-white hover:brightness-95',
  plain: 'bg-transparent text-blue hover:bg-blue-tint-2',
};

const sizes: Record<Size, string> = {
  xs: 'min-h-[30px] rounded-md px-2.5 py-1.5 text-[11px]',
  sm: 'min-h-9 rounded-btn px-3.5 py-2 text-xs',
  md: 'min-h-11 rounded-btn px-[18px] py-3 text-[13.5px]',
  // For the rider app. A person standing at a gate, holding a sealed bag,
  // in sunlight, aiming with one thumb — 44px is the accessibility floor,
  // not a target to design to.
  lg: 'min-h-[56px] rounded-btn px-6 py-4 text-[16px]',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  full?: boolean;
  icon?: IconName;
  iconAfter?: IconName;
  loading?: boolean;
  children?: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  full = false,
  icon,
  iconAfter,
  loading = false,
  disabled,
  className,
  children,
  type = 'button',
  ...props
}: ButtonProps) {
  const iconSize = size === 'xs' ? 12 : size === 'sm' ? 14 : size === 'lg' ? 19 : 15;

  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center gap-[7px] font-bold leading-none',
        'transition-[background-color,box-shadow,filter] duration-150',
        'disabled:cursor-not-allowed disabled:opacity-50',
        variants[variant],
        sizes[size],
        full && 'w-full',
        className,
      )}
      {...props}
    >
      {loading ? (
        <Icon name="refresh" size={iconSize} className="animate-spin" />
      ) : icon ? (
        <Icon name={icon} size={iconSize} strokeWidth={2} />
      ) : null}
      {children}
      {iconAfter && !loading ? <Icon name={iconAfter} size={iconSize} strokeWidth={2} /> : null}
    </button>
  );
}
